/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './SalesTargetListCard.module.scss';

import type { SalesTargetListCardProps } from './SalesTargetListCard.types';

const FILTERS: { label: string; value: string }[] = [
  { label: 'Industry', value: 'UK SIC 62012 · WZ 62.01 · ATECO 62.01' },
  { label: 'Filed revenue', value: '€5M – €50M · latest accounts' },
  { label: 'Signals', value: 'Revenue ▲ 20%+ YoY · EBITDA ▲ 15%+ YoY' },
];

const RESULTS: { company: string; location: string; code: string; revenue: string; yoy: string; open?: boolean }[] = [
  { company: 'Brightline Software Ltd', location: 'Manchester · UK', code: '62012', revenue: '€18.4M', yoy: '▲ 34%' },
  { company: 'Kessler Systeme GmbH', location: 'Stuttgart · DE', code: '62.01', revenue: '€27.9M', yoy: '▲ 26%' },
  {
    company: 'Meridiana Software S.r.l.',
    location: 'Bologna · IT',
    code: '62.01',
    revenue: '€9.6M',
    yoy: '▲ 41%',
    open: true,
  },
];

const EXECS: { initials: string; name: string; role: string; contact: string }[] = [
  {
    initials: 'LR',
    name: 'L. Ricci',
    role: 'Amministratore delegato · per registry',
    contact: 'l.ricci@meridiana-sw.it',
  },
  { initials: 'GM', name: 'G. Moretti', role: 'Direttore finanziario', contact: 'g.moretti@meridiana-sw.it' },
];

const ROW_STAGGER_S = 0.14;
const ROW_START_S = 0.15;

export const SalesTargetListCard = ({ className }: SalesTargetListCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const played = useIntersection(ref, { threshold: 0.35 });

  return (
    <div ref={ref} className={cn(styles.card, played && styles.play, className)}>
      <div className={styles.head}>
        <span className={styles.dot} />
        <span className={styles.headTitle}>Target list — growing software companies, UK · DE · IT</span>
      </div>

      <div className={styles.body}>
        <div className={styles.filters}>
          {FILTERS.map((filter) => (
            <div key={filter.label} className={styles.filter}>
              <span className={styles.filterKey}>{filter.label}</span>
              <span className={styles.filterValue}>{filter.value}</span>
            </div>
          ))}
        </div>

        <div className={styles.table}>
          <div className={styles.tableHead}>
            <span>Company</span>
            <span>Code</span>
            <span>Revenue</span>
            <span>YoY</span>
            <span />
          </div>

          {RESULTS.map((result, i) => (
            <div
              key={result.company}
              className={cn(styles.row, result.open && styles.rowOpen)}
              style={{ transitionDelay: `${ROW_START_S + i * ROW_STAGGER_S}s` }}
            >
              <span className={styles.company}>
                {result.company}
                <i>{result.location}</i>
              </span>
              <span className={styles.code}>{result.code}</span>
              <span>{result.revenue}</span>
              <span className={styles.up}>{result.yoy}</span>
              <button type="button" className={cn(styles.ext, result.open && styles.extOn)} aria-label="Key executives">
                ☷
              </button>
            </div>
          ))}
        </div>

        <div
          className={styles.popover}
          style={{ transitionDelay: `${ROW_START_S + RESULTS.length * ROW_STAGGER_S + 0.15}s` }}
        >
          <div className={styles.popoverHead}>
            <span className={styles.popoverIco}>GD</span>
            <span className={styles.popoverTitle}>Key executives · Meridiana Software S.r.l.</span>
            <span className={styles.popoverTag}>Browser extension</span>
          </div>

          {EXECS.map((exec) => {
            const [contactUser, contactDomain] = exec.contact.split('@');

            return (
              <div key={exec.name} className={styles.exec}>
                <span className={styles.execAvatar}>{exec.initials}</span>
                <span className={styles.execWho}>
                  <b>{exec.name}</b>
                  {exec.role}
                </span>
                <span className={styles.execContact}>
                  <span className={styles.execContactMasked} aria-hidden="true">
                    {contactUser}
                  </span>
                  @{contactDomain}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
